/*
Copyright (c) 2024, HP Development Company, L.P. All rights reserved.
This software contains confidential and proprietary information of HP.
The user of this software agrees not to disclose, disseminate or copy
such Confidential Information and shall use the software only in accordance
with the terms of the license agreement the user entered into with HP.
*/

// https://github.com/WICG/focus-visible/blob/v5.0.2/src/focus-visible.js
import * as React from 'react';
import * as ReactDOM from 'react-dom';

const inputTypesWhitelist = {
  text: true,
  search: true,
  url: true,
  tel: true,
  email: true,
  password: true,
  number: true,
  date: true,
  month: true,
  week: true,
  time: true,
  datetime: true,
  'datetime-local': true
};

function focusTriggersKeyboardModality(el) {
  const { type, tagName } = el;

  if (tagName === 'INPUT' && inputTypesWhitelist[type] && !el.readOnly) {
    return true;
  }

  if (tagName === 'TEXTAREA' && !el.readOnly) {
    return true;
  }

  if (el.isContentEditable) {
    return true;
  }

  return false;
}

export default function useFocusVisible() {
  const hadFocusVisibleRecently = React.useRef(false);
  const hadFocusVisibleRecentlyTimeout = React.useRef();
  const hadKeyboardEvent = React.useRef(true);
  const ownerDocumentRef = React.useRef();

  const ref = React.useCallback((instance) => {
    // eslint-disable-next-line react/no-find-dom-node
    const node = ReactDOM.findDOMNode(instance);
    if (node) {
      ownerDocumentRef.current = node.ownerDocument;
    }
  }, []);

  const handleKeyDown = (event) => {
    if (event.metaKey || event.altKey || event.ctrlKey) {
      return;
    }

    hadKeyboardEvent.current = true;
  };

  const handlePointerDown = () => {
    hadKeyboardEvent.current = false;
  };

  const handleVisibilityChange = () => {
    const { current: ownerDocument } = ownerDocumentRef;
    if (ownerDocument.visibilityState === 'hidden') {
      if (hadFocusVisibleRecently.current) {
        hadKeyboardEvent.current = true;
      }
    }
  };

  React.useEffect(() => {
    const { current: ownerDocument } = ownerDocumentRef;
    if (ownerDocument) {
      ownerDocument.addEventListener('keydown', handleKeyDown, true);
      ownerDocument.addEventListener('mousedown', handlePointerDown, true);
      ownerDocument.addEventListener('pointerdown', handlePointerDown, true);
      ownerDocument.addEventListener('touchstart', handlePointerDown, true);
      ownerDocument.addEventListener(
        'visibilitychange',
        handleVisibilityChange,
        true
      );
    }

    return () => {
      if (ownerDocument) {
        ownerDocument.removeEventListener('keydown', handleKeyDown, true);
        ownerDocument.removeEventListener('mousedown', handlePointerDown, true);
        ownerDocument.removeEventListener(
          'pointerdown',
          handlePointerDown,
          true
        );
        ownerDocument.removeEventListener(
          'touchstart',
          handlePointerDown,
          true
        );
        ownerDocument.removeEventListener(
          'visibilitychange',
          handleVisibilityChange,
          true
        );
        clearTimeout(hadFocusVisibleRecentlyTimeout.current);
      }
    };
  }, [ownerDocumentRef]);

  /**
   * Returns `true` if the event is called by keyboard key
   * @param {*} event
   */
  const handleFocusVisible = (event) => {
    const { target } = event;

    try {
      target.matches(':focus-visible');
      // eslint-disable-next-line no-empty
    } catch (e) {}

    return hadKeyboardEvent.current || focusTriggersKeyboardModality(target);
  };

  const handleBlurVisible = () => {
    hadFocusVisibleRecently.current = true;
    clearTimeout(hadFocusVisibleRecentlyTimeout.current);
    hadFocusVisibleRecentlyTimeout.current = setTimeout(() => {
      hadFocusVisibleRecently.current = false;
    }, 100);
  };

  return { handleBlurVisible, handleFocusVisible, ref };
}
